import { Link, useLocation } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { LEGAL_PAGES, LEGAL_NAV } from '../data/legal';

function RichText({ text }) {
  const parts = text.split(/(\[[^\]]+\])/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('[') && part.endsWith(']') ? (
          <mark
            key={i}
            className="rounded bg-lime px-1 py-0.5 font-medium not-italic text-charcoal"
            title="Placeholder — confirm before launch"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default function LegalPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace('/', '');
  const page = LEGAL_PAGES[slug];
  if (!page) return null;

  return (
    <>
      <Seo
        title={`${page.title} | Build My Trade`}
        description={page.intro.slice(0, 155)}
        path={`/${slug}`}
      />
      <PageHero
        eyebrow="Legal"
        title={page.title}
        intro={page.intro}
        crumbs={[{ label: page.title }]}
        testid={`legal-heading-${slug}`}
      />
      <section className="bg-offwhite" data-testid={`legal-${slug}`}>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-12">
            <aside className="lg:col-span-3">
              <nav aria-label="Legal pages" className="lg:sticky lg:top-28">
                <ul className="flex flex-wrap gap-2 lg:flex-col">
                  {LEGAL_NAV.map((l) => (
                    <li key={l.slug}>
                      <Link
                        to={`/${l.slug}`}
                        aria-current={l.slug === slug ? 'page' : undefined}
                        data-testid={`legal-nav-${l.slug}`}
                        className={`inline-block rounded-full px-4 py-2 text-sm font-medium transition-colors lg:rounded-xl ${
                          l.slug === slug
                            ? 'bg-charcoal text-offwhite'
                            : 'text-charcoal/70 hover:bg-charcoal/5 hover:text-charcoal'
                        }`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>
            <div className="lg:col-span-9">
              <Reveal>
                <p className="mb-10 rounded-xl border border-hairline bg-white px-5 py-4 text-xs leading-relaxed text-stonebrand">
                  These policies reflect how this website and business actually operate today. If the way we handle
                  data, payments or services changes, we’ll update the relevant policy to match.
                </p>
              </Reveal>
              <div className="space-y-10">
                {page.sections.map((section, i) => (
                  <Reveal key={i} delay={0.04 * i}>
                    <article className="rounded-2xl border border-hairline bg-white p-7 sm:p-9">
                      <h2 className="font-heading text-xl font-bold tracking-tight text-charcoal sm:text-2xl">
                        {section.h}
                      </h2>
                      {section.bullets && (
                        <ul className="mt-4 space-y-2.5">
                          {section.bullets.map((b, j) => (
                            <li key={j} className="flex gap-3 text-sm leading-relaxed text-stonebrand sm:text-base">
                              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-charcoal/40" aria-hidden="true" />
                              <span>
                                <RichText text={b} />
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {section.paragraphs?.map((p, j) => (
                        <p key={j} className="mt-4 text-sm leading-relaxed text-stonebrand sm:text-base">
                          <RichText text={p} />
                        </p>
                      ))}
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
